import { memo, useMemo, useRef, useState } from 'react';
import { Button, message, Table } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useRequest, useSize } from 'ahooks';
import dayjs from 'lib/dayjs';
import { getTodoOverviewList } from './api';
import { formatDuration } from './Overview';
import produce from 'immer';
import useModal from 'hooks/useModal';
import TodoDrawer from 'views/Todo/components/TodoDrawer';
import { useTypeLabel } from 'views/Todo/hooks/useLabel';
import { useStatusLabel } from 'views/Todo/hooks/useLabel';
import { getTodoDetail } from '../../Conversation/Sider/ToDoBundle/api';
import { childStatusI18nMap } from 'views/Conversation/Sider/ToDoBundle/childStatusI18nMap';

const StoreOverview = memo((props: { values: Todo.Data.Params }) => {
  const { values } = props;
  const { t } = useTranslate();
  const tableDomRef = useRef<HTMLDivElement>(null);
  const height = useSize(tableDomRef)?.height;
  const [pageInfo, setPageInfo] = useState<{ pageSize: number; pageNum: number }>({
    pageSize: 10,
    pageNum: 1,
  });
  const { getTypeLabel } = useTypeLabel();
  const { statusLabel } = useStatusLabel();
  const { visible, show, hide, meta } = useModal<Todo.Record>();
  const drawer = meta && (
    <TodoDrawer todo={meta} visible={visible} onClose={() => hide()} readonly />
  );
  const { runAsync, loading } = useRequest((id) => getTodoDetail(id), {
    manual: true,
    onError: () => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    },
  });

  const params = useMemo(() => {
    return {
      ...values,
      ...pageInfo,
    };
  }, [values, pageInfo]);
  const { data } = useRequest<{ todoOverviews: Todo.Data.Overview[] }>(
    () => getTodoOverviewList(params),
    {
      refreshDeps: [params],
      onError: () => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      },
    },
  );
  const tableDataSource = data?.todoOverviews;
  const total = data?.count;

  return (
    <div className="bg-[var(--fett-bg-panel)] flex-grow overflow-auto" ref={tableDomRef}>
      <div className="text-[var(--fett-text-primary)] text-[16px] font-medium h-[56px] py-[17px] px-[24px] bg-[var(--fett-bg-panel)] border-solid border-0 border-b border-[var(--fett-border-default)]">
        {t('ViewDashboard.待办处理情况')}
      </div>
      <div className="p-[16px] pb-0">
        {drawer}
        <Table
          dataSource={tableDataSource || []}
          scroll={{ y: height - 228 }}
          rowKey="todoInstanceId"
          columns={[
            {
              title: t('phrase.店铺名称'),
              key: 'storeName',
              dataIndex: 'storeName',
              render: (value, record) => {
                return value || record.storeAlias;
              },
            },
            {
              title: t('marketing.买家昵称'),
              key: 'buyerNick',
              dataIndex: 'buyerNick',
            },
            {
              title: t('todo.待办类型'),
              key: 'todoType',
              dataIndex: 'todoType',
              render: (value, record) => {
                let str = getTypeLabel(value);
                if (value === getTypeLabel(value)) {
                  str = record.typeName;
                }

                if (record.todoSubType) {
                  str += `-${record.todoSubType}`;
                }
                if (record.todoSubType2) {
                  str += `-${record.todoSubType2}`;
                }
                if (record.todoSubType3) {
                  str += `-${record.todoSubType3}`;
                }

                return str;
              },
            },
            {
              title: t('todo.待办状态'),
              key: 'todoStatus',
              dataIndex: 'todoStatus',
              render: (value, record) => {
                let str = statusLabel[value];
                if (record.todoSubStatus) {
                  str += `-${
                    childStatusI18nMap[record.todoSubStatus]
                      ? t(childStatusI18nMap[record.todoSubStatus])
                      : record.todoSubStatus
                  }`;
                }
                return str;
              },
            },
            {
              title: t('ViewDashboard.待办创建时间'),
              key: 'todoCreateTime',
              dataIndex: 'todoCreateTime',
              width: 200,
              render: (value) => {
                return dayjs(Number(value)).format('YYYY-MM-DD HH:mm:ss');
              },
            },
            {
              title: t('ViewDashboard.首次跟进待办间隔时长'),
              key: 'todoProcessingSeconds',
              dataIndex: 'todoProcessingSeconds',
              render: (value, record) => {
                if (record.todoStatus === 1) {
                  return undefined;
                } else {
                  return formatDuration(value);
                }
              },
            },
            {
              title: t('ViewDashboard.完成待办时间'),
              key: 'todoCompletedTime',
              dataIndex: 'todoCompletedTime',
              width: 200,
              render: (value, record) => {
                if (record.todoStatus !== 3) {
                  return undefined;
                } else {
                  return dayjs(Number(value)).format('YYYY-MM-DD HH:mm:ss');
                }
              },
            },
            {
              title: t('ViewDashboard.完成待办耗时'),
              key: 'todoCompletedSeconds',
              dataIndex: 'todoCompletedSeconds',
              render: (value, record) => {
                if (record.todoStatus !== 3) {
                  return undefined;
                } else {
                  return formatDuration(value);
                }
              },
            },
            {
              key: 'operation',
              title: t('操作'),
              width: 100,
              fixed: 'right',
              render: (_, record) => (
                <Button
                  type="link"
                  loading={loading}
                  onClick={() => {
                    const { todoInstanceId: id } = record;

                    runAsync(id).then((res) => {
                      show(res);
                    });
                  }}
                  style={{ paddingLeft: 0 }}
                >
                  {t('feedback.详情')}
                </Button>
              ),
            },
          ]}
          pagination={{
            total,
            showTotal: (x) => t('feedback.共x项', { x }),
            onChange: (pageNum, pageSize) => {
              setPageInfo(
                produce(pageInfo, (draft) => {
                  draft.pageNum = pageNum;
                  draft.pageSize = pageSize;
                }),
              );
            },
          }}
        ></Table>
      </div>
    </div>
  );
});

export default StoreOverview;
